import React from 'react';
import { ChevronRight, Database, Layers3, Search } from 'lucide-react';
import { Button } from '@daater/ui';
import type { SavedReportItem } from '../api/types';
import {
  useDatasetsQuery,
  useDeleteSavedReportMutation,
  useSavedReportsQuery,
} from '../api/queries';
import { useAccountStore } from '../lib/account-store';
import SavedSearchesSection from '../components/saved-searches-section';
import { useOpenSavedSearch } from '../hooks/useOpenSavedSearch';
import { extractApiErrorMessage } from './table-data-detail.utils';

export default function ReportsScreen() {
  const account = useAccountStore(state => state.account);
  const { data: datasets = [], isLoading: datasetsLoading } = useDatasetsQuery(true);
  const [selectedDatasetId, setSelectedDatasetId] = React.useState<string | undefined>(
    account?.dataset
  );
  const [selectedTableId, setSelectedTableId] = React.useState('all');
  const [screenError, setScreenError] = React.useState<string | null>(null);
  const [openingSavedReportId, setOpeningSavedReportId] = React.useState<string | null>(null);
  const [deletingSavedReportId, setDeletingSavedReportId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (selectedDatasetId) return;
    if (!datasets.length) return;
    setSelectedDatasetId(account?.dataset ?? datasets[0]?._id);
  }, [account?.dataset, datasets, selectedDatasetId]);

  React.useEffect(() => {
    setSelectedTableId('all');
  }, [selectedDatasetId]);

  const selectedDataset = React.useMemo(
    () => datasets.find(dataset => dataset._id === selectedDatasetId),
    [datasets, selectedDatasetId]
  );
  const tableOptions = selectedDataset?.tables ?? [];
  const { data: savedReportsData, isLoading: savedReportsLoading } =
    useSavedReportsQuery(selectedDatasetId);
  const savedReports = React.useMemo(() => savedReportsData?.items ?? [], [savedReportsData]);
  const filteredReports = React.useMemo(() => {
    if (selectedTableId === 'all') return savedReports;
    return savedReports.filter(report => report.table === selectedTableId);
  }, [savedReports, selectedTableId]);
  const openSavedSearch = useOpenSavedSearch();
  const deleteSavedReportMutation = useDeleteSavedReportMutation(selectedDatasetId);

  const handleOpenSavedReport = React.useCallback(
    async (report: SavedReportItem, fallbackDataset?: string) => {
      try {
        setOpeningSavedReportId(report._id);
        setScreenError(null);
        await openSavedSearch(report, fallbackDataset);
      } catch (error) {
        const message = extractApiErrorMessage(error) || 'No se pudo abrir la búsqueda guardada.';
        setScreenError(message);
      } finally {
        setOpeningSavedReportId(null);
      }
    },
    [openSavedSearch]
  );

  const handleDeleteSavedReport = React.useCallback(
    async (report: SavedReportItem) => {
      const confirmed = window.confirm(
        `¿Eliminar la búsqueda guardada "${report.name ?? 'Sin nombre'}"?`
      );
      if (!confirmed) return;

      try {
        setDeletingSavedReportId(report._id);
        setScreenError(null);
        await deleteSavedReportMutation.mutateAsync(report._id);
      } catch (error) {
        const message =
          extractApiErrorMessage(error) || 'No se pudo eliminar la búsqueda guardada.';
        setScreenError(message);
      } finally {
        setDeletingSavedReportId(null);
      }
    },
    [deleteSavedReportMutation]
  );

  return (
    <section className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-6 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-[28px] border border-pale-grey-three bg-white px-6 py-8 shadow-[0_24px_50px_-40px_rgba(15,23,42,0.3)] sm:px-8">
        <div className="relative grid gap-6 lg:grid-cols-[1.35fr_0.65fr] lg:items-end">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
              <Layers3 className="h-3.5 w-3.5" />
              Centro de búsquedas guardadas
            </div>
            <div className="max-w-3xl">
              <h1 className="text-2xl font-semibold leading-tight text-charcoal-grey sm:text-3xl">
                Reabre tus consultas importantes sin reconstruir cada filtro.
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate sm:text-base">
                Este espacio concentra las búsquedas persistidas en Daater para que tu equipo
                trabaje con contexto estable, rápido y fácil de retomar.
              </p>
            </div>
          </div>

          <div className="grid gap-3 rounded-[24px] border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm">
              <Database className="h-5 w-5 text-primary" />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate/70">Base activa</div>
                <div className="font-medium text-charcoal-grey">
                  {selectedDataset?.name ??
                    (datasetsLoading ? 'Cargando...' : 'Selecciona una base')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm">
              <Search className="h-5 w-5 text-primary" />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate/70">Búsquedas</div>
                <div className="font-medium text-charcoal-grey">
                  {savedReports.length.toLocaleString('es-CO')} guardadas en este dataset
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_24px_50px_-40px_rgba(15,23,42,0.45)] lg:grid-cols-[1fr_1fr_auto] lg:items-end">
        <label className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate">
            Dataset
          </span>
          <select
            value={selectedDatasetId ?? ''}
            onChange={event => setSelectedDatasetId(event.target.value || undefined)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-primary focus:bg-white focus:ring focus:ring-primary/15"
          >
            {datasets.map(dataset => (
              <option key={dataset._id} value={dataset._id}>
                {dataset.name ?? dataset._id}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate">
            Tabla
          </span>
          <select
            value={selectedTableId}
            onChange={event => setSelectedTableId(event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-primary focus:bg-white focus:ring focus:ring-primary/15"
          >
            <option value="all">Todas las tablas</option>
            {tableOptions.map(table => (
              <option key={table._id} value={table._id}>
                {table.name ?? table._id}
              </option>
            ))}
          </select>
        </label>

        <Button
          variant="outline"
          className="rounded-full border-slate-200 bg-white/90 px-4 text-slate-700 hover:border-primary/30 hover:bg-primary/5"
          onClick={() => {
            setSelectedDatasetId(account?.dataset ?? datasets[0]?._id);
            setSelectedTableId('all');
          }}
        >
          Volver al dataset activo
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {screenError ? (
        <div className="rounded-2xl border border-darkish-pink/30 bg-darkish-pink/5 px-4 py-3 text-sm text-darkish-pink">
          {screenError}
        </div>
      ) : null}

      <SavedSearchesSection
        reports={filteredReports}
        datasets={datasets}
        currentDatasetId={selectedDatasetId}
        currentTableId={selectedTableId === 'all' ? undefined : selectedTableId}
        loading={savedReportsLoading}
        openingReportId={openingSavedReportId}
        deletingReportId={deletingSavedReportId}
        onOpen={handleOpenSavedReport}
        onDelete={handleDeleteSavedReport}
        showScopeFilter={false}
        emptyMessage="No hay búsquedas guardadas para la combinación seleccionada."
      />
    </section>
  );
}
